import { useState, useEffect } from "react";
import { t, font, Button, Badge } from "@realtyflows/ui";
import { getDealDocuments, getDealMilestones, resendDocSign, voidDocSign, completeMilestone, getSignedPdfUrl } from "../api";

const statusColors = {
  signed: t.emerald,
  partially_signed: t.sky,
  sent: t.sky,
  pending: t.amber,
  awaiting: t.amber,
  declined: t.coral,
  voided: t.textMuted,
  complete: t.emerald,
};

const statusLabels = {
  signed: "Fully Signed",
  partially_signed: "Agent Signed — Awaiting Client",
  sent: "Sent for Signing",
  pending: "Pending",
  awaiting: "Awaiting",
  declined: "Declined",
  voided: "Voided",
  complete: "Complete",
};

export default function DealDocumentTimeline({ dealId, compact = false }) {
  const [documents, setDocuments] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // docId or milestoneId being acted on
  const [toast, setToast] = useState(null);
  const [confirmVoid, setConfirmVoid] = useState(null); // docId to confirm void

  useEffect(() => {
    if (!dealId) return;
    Promise.all([
      getDealDocuments(dealId).catch(() => []),
      getDealMilestones(dealId).catch(() => []),
    ])
      .then(([docs, ms]) => { setDocuments(docs); setMilestones(ms); })
      .finally(() => setLoading(false));
  }, [dealId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (message, variant = "success") => setToast({ message, variant });

  const handleResend = async (docId) => {
    setActionLoading(docId);
    try {
      await resendDocSign(dealId, docId);
      showToast("Reminder sent successfully");
    } catch (err) {
      showToast(err.message || "Failed to resend", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleVoid = async (docId) => {
    setActionLoading(docId);
    try {
      await voidDocSign(dealId, docId);
      setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, status: "voided" } : d));
      showToast("Document voided");
      setConfirmVoid(null);
    } catch (err) {
      showToast(err.message || "Failed to void", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (milestoneId) => {
    setActionLoading(`m-${milestoneId}`);
    // Optimistic update
    setMilestones((prev) => prev.map((m) => m.id === milestoneId ? { ...m, status: "complete", completed_at: new Date().toISOString() } : m));
    try {
      await completeMilestone(dealId, milestoneId);
      showToast("Milestone completed");
    } catch (err) {
      // Revert on failure
      setMilestones((prev) => prev.map((m) => m.id === milestoneId ? { ...m, status: "pending", completed_at: null } : m));
      showToast(err.message || "Failed to complete", "error");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <div style={{ padding: compact ? 6 : 20, fontSize: compact ? 10 : 12, color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }

  const items = [
    ...documents.map((d) => ({ ...d, itemType: "document", sortDate: d.created_at })),
    ...milestones.map((m) => ({ ...m, itemType: "milestone", sortDate: m.created_at })),
  ].sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate));

  if (items.length === 0) {
    return (
      <div style={{ padding: compact ? 6 : 20, textAlign: "center", fontSize: compact ? 10 : 12, color: t.textMuted, fontFamily: font.body }}>
        No documents or milestones yet
      </div>
    );
  }

  // ── Compact mode: minimal rows for pipeline cards ──
  if (compact) {
    return (
      <div>
        {items.map((item) => {
          const isDoc = item.itemType === "document";
          const status = item.status || "pending";
          const color = statusColors[status] || t.textMuted;
          const done = status === "signed" || status === "complete";
          return (
            <div key={`${item.itemType}-${item.id}`} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "3px 0",
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                background: done ? t.emerald : "transparent",
                border: `1.5px solid ${color}`,
              }} />
              <span style={{
                fontSize: 10, color: t.textPrimary, fontFamily: font.body, fontWeight: 500,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
              }}>
                {isDoc ? (item.template_name || item.name) : item.name}
              </span>
              <span style={{
                fontSize: 8, fontWeight: 700, color, fontFamily: font.mono,
                padding: "1px 4px", borderRadius: 3, background: `${color}12`, flexShrink: 0,
              }}>
                {done ? (isDoc ? "Signed" : "Done") : status === "sent" ? "Sent" : status === "declined" ? "Declined" : status === "voided" ? "Void" : "Pending"}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Full mode ──
  return (
    <div style={{ position: "relative" }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.variant === "error" ? t.coral : t.emerald,
          color: "#fff", padding: "10px 20px", borderRadius: 8,
          fontSize: 12, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          animation: "fadeSlide 0.3s ease",
        }}>
          {toast.message}
        </div>
      )}

      {/* Void Confirm Dialog */}
      {confirmVoid && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 998,
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setConfirmVoid(null)}>
          <div style={{
            background: t.surface, borderRadius: 12, padding: 24, maxWidth: 360,
            border: `1px solid ${t.coral}30`, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
              Void Document?
            </div>
            <div style={{ fontSize: 12, color: t.textSecondary, marginBottom: 20, lineHeight: 1.6, fontFamily: font.body }}>
              This will cancel the e-signature request. All pending signatures will be invalidated. This action cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Button variant="outline" size="sm" onClick={() => setConfirmVoid(null)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={() => handleVoid(confirmVoid)}
                style={{ background: t.coral, borderColor: t.coral }}
              >
                {actionLoading === confirmVoid ? "Voiding..." : "Void Document"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div style={{ position: "relative", paddingLeft: 28 }}>
        {/* Vertical line */}
        <div style={{
          position: "absolute", left: 9, top: 4, bottom: 4, width: 2,
          background: `linear-gradient(to bottom, ${t.border}, ${t.emerald}30)`,
        }} />

        {items.map((item) => {
          const isDoc = item.itemType === "document";
          const status = item.status || "pending";
          const color = statusColors[status] || t.textMuted;

          return (
            <div key={`${item.itemType}-${item.id}`} style={{ position: "relative", marginBottom: 16 }}>
              {/* Dot */}
              <div style={{
                position: "absolute", left: -22, top: 8,
                width: 12, height: 12, borderRadius: "50%",
                background: status === "signed" || status === "complete" ? t.emerald : t.surface,
                border: `2px solid ${color}`,
              }} />

              {/* Card */}
              <div style={{
                background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10,
                padding: "12px 16px",
                borderLeft: `3px solid ${color}`,
              }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                      {isDoc ? (item.template_name || item.name) : item.name}
                    </span>
                    <Badge variant={status === "signed" || status === "complete" ? "emerald" : status === "declined" ? "coral" : "sky"}>
                      {statusLabels[status] || status}
                    </Badge>
                  </div>
                  {isDoc && item.template_description && (
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, marginBottom: 4, lineHeight: 1.4 }}>
                      {item.template_description}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 6 }}>
                    {new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {item.signed_at && ` \u00B7 Signed ${new Date(item.signed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                    {item.completed_at && ` \u00B7 Done ${new Date(item.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {isDoc && status === "sent" && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleResend(item.id)}
                          disabled={actionLoading === item.id}
                        >
                          {actionLoading === item.id ? "..." : "Resend"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmVoid(item.id)}
                          style={{ color: t.coral, borderColor: `${t.coral}40` }}
                        >
                          Void
                        </Button>
                      </>
                    )}

                    {isDoc && status === "pending" && item.esign_envelope_id && (
                      <Button variant="outline" size="sm" onClick={() => setConfirmVoid(item.id)}
                        style={{ color: t.coral, borderColor: `${t.coral}40` }}
                      >
                        Void
                      </Button>
                    )}

                    {isDoc && item.esign_envelope_id && status !== "voided" && (
                      <Button variant="outline" size="sm" onClick={() => window.open(getSignedPdfUrl(dealId, item.id), "_blank")}>
                        View
                      </Button>
                    )}

                    {!isDoc && status !== "complete" && (
                      <Button variant="primary" size="sm" onClick={() => handleComplete(item.id)}
                        disabled={actionLoading === `m-${item.id}`}
                      >
                        {actionLoading === `m-${item.id}` ? "..." : "Done"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
